import React from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useAuthorization } from '../components/login/use-authorization';

type ProtectedRouteProps = {
  children: React.ReactNode;
  requiredPermissions: string[];
  redirectTo?: string;
};

/**
 * ProtectedRoute wrapper component that checks if the user has
 * the required permissions before rendering children.
 * 
 * This component should wrap route components to enforce
 * permission-based access control at the route level.
 * 
 * @param children - The route component to render if authorized
 * @param requiredPermissions - Array of permissions, user needs at least one
 * @param redirectTo - Optional path to redirect to if unauthorized (defaults to /students)
 */
export function ProtectedRoute({
  children,
  requiredPermissions,
  redirectTo = '/students',
}: ProtectedRouteProps) {
  const { hasAnyPermission, isLoading } = useAuthorization();
  const navigate = useNavigate();

  // Check if user has required permissions (after loading)
  const hasPermission = React.useMemo(
    () => !isLoading && hasAnyPermission(requiredPermissions),
    [isLoading, hasAnyPermission, requiredPermissions]
  );

  // Redirect effect - must be at top level to follow Rules of Hooks
  React.useEffect(() => {
    // Only redirect if done loading and user lacks permission
    if (!isLoading && !hasPermission) {
      navigate({ to: redirectTo });
    }
  }, [isLoading, hasPermission, navigate, redirectTo]);

  // While checking permissions, show loading state
  if (isLoading) {
    return (
      <div className="flex min-h-[400px] items-center justify-center">
        <div className="text-center">
          <div className="mb-2 text-muted-foreground">Checking permissions...</div>
        </div>
      </div>
    );
  }

  // If no permission, show access denied message while redirecting
  if (!hasPermission) {
    return (
      <div className="flex min-h-[400px] items-center justify-center">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-destructive mb-2">Access Denied</h2>
          <p className="text-muted-foreground">
            You don't have permission to access this page.
          </p>
          <p className="text-sm text-muted-foreground mt-2">Redirecting...</p>
        </div>
      </div>
    );
  }

  // User has permission, render children
  return <>{children}</>;
}

